import React, { useMemo } from "react"

import { gql, useQuery } from "urql"

import { ChevronRightIcon } from "@heroicons/react/24/outline"

import { H4, WithLoadingIndicator } from "~tailwindui/Basics"

import ordered from "src/ordered"

import useWebsocket from "hooks/useWebsocket"

import LessonResource from "./Details"
import LessonPlan from "./LessonPlan"

export type LessonResourcesProps = {
  airtutorsSessionId: string
}

const LessonResources: React.FC<LessonResourcesProps> = ({
  airtutorsSessionId,
}) => {
  const [result] = useQuery({
    query: lessonResourcesQuery,
    variables: { airtutorsSessionId },
  })

  const channelProps = useMemo(() => {
    return { id: airtutorsSessionId }
  }, [airtutorsSessionId])

  useWebsocket({
    channelName: "SessionChannel",
    channelProps,
  })

  if (result.data && !result.data.airtutorsSession) return null

  return (
    <div>
      <WithLoadingIndicator result={result}>
        {({ data: { airtutorsSession } }) => (
          <>
            <H4 className="mb-2">Current Lesson Resources</H4>
            <ul className="inline-block divide-y text-left">
              {ordered(
                ordered(
                  ordered(airtutorsSession.currentLessonResources, "sortOrder"),
                  "lessonTopic",
                  "sortOrder"
                ),
                "lessonPlan",
                "name"
              ).map(lessonResource => (
                <li key={lessonResource.id} className="py-4 md:py-2">
                  <div>
                    {lessonResource.lessonPlan.name}
                    <ChevronRightIcon className="inline-block h-8 w-8 px-2" />
                    {lessonResource.lessonTopic.name}
                  </div>
                  <LessonResource
                    key={lessonResource.id}
                    airtutorsSessionId={airtutorsSessionId}
                    lessonResource={lessonResource}
                    isCurrent={true}
                  />
                </li>
              ))}
            </ul>

            <div className="mt-4 text-left">
              <H4 className="mb-2">All Lesson Plans</H4>
              {airtutorsSession.lessonPlans.map(lessonPlan => (
                <LessonPlan
                  key={lessonPlan.id}
                  sessionId={airtutorsSessionId}
                  lessonPlanId={lessonPlan.id}
                  name={lessonPlan.name}
                />
              ))}
            </div>
          </>
        )}
      </WithLoadingIndicator>
    </div>
  )
}

const lessonResourcesQuery = gql`
  query SessionLessonResources($airtutorsSessionId: ID!) {
    airtutorsSession(id: $airtutorsSessionId) {
      lessonPlans {
        id
        name
      }
      currentLessonResources {
        id
        name
        sortOrder
        file {
          thumbUrl
          previewUrl
          downloadUrl
          filename
        }
        lessonTopic {
          id
          name
          sortOrder
        }
        lessonPlan {
          id
          name
        }
        lessonResourceCompletions(sessionId: $airtutorsSessionId) {
          id
          createdAt
          studentMastery
          student {
            id
            fullName
          }
        }
      }
    }
  }
`

export default LessonResources
